 import React, { useState, useEffect } from 'react';
 import { useParams, useNavigate } from 'react-router-dom';
 import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
 import { API_ENDPOINTS } from '../../config/api';
 import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Chama {
  id: string;
  name: string;
  description?: string;
  type: string;
  status: string;
  avatar_url?: string;
  cover_image_url?: string;
  created_at: string;
  contribution_amount: number;
  contribution_frequency: string;
  max_members: number;
  current_members: number;
  location?: string;
  rules?: string[];
  category?: string;
  is_private: boolean;
  chairperson_id?: string;
  treasurer_id?: string;
  secretary_id?: string;
  county?: string;
  town?: string;
  total_funds?: number;
}

interface User {
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  is_online?: boolean;
  status?: string;
}

interface Member {
  id: string;
  user_id: string;
  role: string;
  status: string;
  joined_at: string;
  is_active?: boolean;
  user?: User;
  first_name?: string;
  last_name?: string;
  total_contributions?: number;
  last_contribution_date?: string;
  last_contribution_amount?: number;
  attendance_rate?: number;
  loan_balance?: number;
  savings_balance?: number;
  reputation_score?: number;
  business_type?: string;
  location?: string;
  phone_verified?: boolean;
  email_verified?: boolean;
}

interface Meeting {
  id: string;
  chamaId?: string;
  title: string;
  description?: string;
  scheduledAt: string;
  duration?: number;
  location?: string;
  meetingUrl?: string;
  meetingType?: string;
  type?: string;
  status: string;
  createdBy?: string;
  createdAt?: string;
  attendees?: number;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    fullName?: string;
  };
}

interface Transaction {
  id: string;
  type: string;
  amount: number;
  created_at: string;
  user_id?: string;
  description?: string;
  status?: string;
  reference?: string;
  initiated_by?: string;
  recipient_id?: string;
  chama_id?: string;
}

interface Poll {
  id: string;
  title: string;
  description?: string;
  status: string;
  end_date: string;
  total_votes: number;
  options?: Array<{
    id: string;
    text: string;
    votes: number;
  }>;
  created_by?: string;
  created_at?: string;
  type?: string;
  is_active?: boolean;
  result?: any;
}

const ChamaDetails: React.FC<{ chamaId?: string }> = ({ chamaId: propChamaId }) => {
    const { chamaId: paramChamaId } = useParams<{ chamaId: string }>();
    const chamaId = paramChamaId || propChamaId || '';
    const colors = getThemeColors('dark');
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

  const [chama, setChama] = useState<Chama | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAvatarMember, setSelectedAvatarMember] = useState<Member | null>(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [userMembership, setUserMembership] = useState<Member | null>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);

  useEffect(() => {
    if (chamaId) {
      loadChamaData();
    }
  }, [chamaId]);

  // Load additional data after chama data is loaded
  useEffect(() => {
    if (chama && chamaId) {
      loadAdditionalData();
      loadCurrentUserProfile();
    }
  }, [chama, chamaId]);

  const loadChamaData = async () => {
    if (!chamaId) return;

    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaDetails: No auth token found');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Real API call to get chama details
      const response = await fetch(API_ENDPOINTS.CHAMA_DETAILS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ ChamaDetails: Failed to load chama details:', errorData);
        alert(`Failed to load chama details: ${errorData.error || 'Unknown error'}`);
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        setChama(data.data);
      } else {
        console.error('❌ ChamaDetails: Invalid chama data structure:', data);
        alert('Invalid chama data received from server');
      }
    } catch (error) {
      console.error('❌ ChamaDetails: Error loading chama details:', error);
      alert('Network error: Unable to connect to the server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };


  const onRefresh = async () => {
    setRefreshing(true);
    await loadChamaData();
    setRefreshing(false);
  };

  // Load current user profile for avatar display
  const loadCurrentUserProfile = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCurrentUserProfile(data.data);
        }
      }
    } catch (error) {
      console.error('❌ ChamaDetails: Error loading current user profile:', error);
    }
  };

  // Load additional chama data (members, meetings, transactions, etc.)
  const loadAdditionalData = async () => {
    if (!chamaId) return;

    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      // Load members
      const membersResponse = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (membersResponse.ok) {
        const membersData = await membersResponse.json();
        if (membersData.success && membersData.data) {
          console.log('✅ ChamaDetails: Successfully loaded members data:', membersData.data.length, 'members');
          setMembers(membersData.data);
          // Load user membership from members data
          const userId = localStorage.getItem('userId');
          const userMembership = membersData.data.find((member: any) => member.user_id === userId);
          setUserMembership(userMembership || null);
        }
      } else {
        console.error('❌ ChamaDetails: Failed to load members data:', membersResponse.status);
      }

      // Load meetings - skip if CORS error to avoid blocking other data
      try {
        const meetingsResponse = await fetch(API_ENDPOINTS.CHAMA_MEETINGS(chamaId), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (meetingsResponse.ok) {
          const meetingsData = await meetingsResponse.json();
          if (meetingsData.success && meetingsData.data) {
            console.log('✅ ChamaDetails: Successfully loaded meetings data:', meetingsData.data.length, 'meetings');
            setMeetings(meetingsData.data);
          }
        } else {
          console.error('❌ ChamaDetails: Failed to load meetings data:', meetingsResponse.status);
        }
      } catch (meetingsError) {
        console.error('❌ ChamaDetails: Error loading meetings (CORS or network issue):', meetingsError);
        // Set empty meetings array instead of failing completely
        setMeetings([]);
      }

      // Load transactions
      const transactionsResponse = await fetch(API_ENDPOINTS.CHAMA_TRANSACTIONS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        if (transactionsData.success && transactionsData.data) {
          console.log('✅ ChamaDetails: Successfully loaded transactions data:', transactionsData.data.length, 'transactions');
          setTransactions(transactionsData.data);
        }
      } else {
        console.error('❌ ChamaDetails: Failed to load transactions data:', transactionsResponse.status);
      }

      // Load polls - skip if CORS error to avoid blocking other data
      try {
        const pollsResponse = await fetch(API_ENDPOINTS.CHAMA_POLLS(chamaId), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (pollsResponse.ok) {
          const pollsData = await pollsResponse.json();
          if (pollsData.success && pollsData.data) {
            console.log('✅ ChamaDetails: Successfully loaded polls data:', pollsData.data.length, 'polls');
            setPolls(pollsData.data);
          }
        } else {
          console.error('❌ ChamaDetails: Failed to load polls data:', pollsResponse.status);
        }
      } catch (pollsError) {
        console.error('❌ ChamaDetails: Error loading polls (CORS or network issue):', pollsError);
        // Set empty polls array instead of failing completely
        setPolls([]);
      }

      // Load statistics
      const statisticsResponse = await fetch(API_ENDPOINTS.CHAMA_STATISTICS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (statisticsResponse.ok) {
        const statisticsData = await statisticsResponse.json();
        if (statisticsData.success && statisticsData.data) {
          console.log('✅ ChamaDetails: Successfully loaded statistics data');
          setStatistics(statisticsData.data);
        }
      } else {
        console.error('❌ ChamaDetails: Failed to load statistics data:', statisticsResponse.status);
      }

    } catch (apiError) {
      console.error('❌ ChamaDetails: Error loading additional data:', apiError);
      // Set empty arrays on error to avoid showing dummy data
      setMembers([]);
      setMeetings([]);
      setTransactions([]);
      setPolls([]);
      setStatistics(null);
      setUserMembership(null);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return colors.success;
      case 'inactive':
        return colors.textSecondary;
      case 'suspended':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  };

  const getRoleColor = (role: string): string => {
    switch (role) {
      case 'chairperson':
        return colors.warning;
      case 'treasurer':
        return colors.warning;
      case 'secretary':
        return colors.warning;
      case 'assistant':
        return colors.secondary;
      default:
        return colors.textSecondary;
    }
  };

  const renderChamaHeader = () => {
    if (!chama) return null;

    // Determine if it's a chama or contribution group
    const isContributionGroup = chama.category === 'contribution';
    const typeConfig = isContributionGroup ? {
      color: colors.success,
      icon: '❤️',
      label: 'Fund Group', // Shorter for mobile
      fullLabel: 'Contribution Group',
      bgColor: colors.success + '15'
    } : {
      color: colors.primary,
      icon: '👥',
      label: 'Chama',
      fullLabel: 'Chama',
      bgColor: colors.primary + '15'
    };

    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
        borderLeftWidth: 4,
        borderLeftColor: typeConfig.color,
      }}>
        {/* Category Badge */}
        <div style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          padding: `${spacing.xs} ${spacing.sm}`,
          borderRadius: borderRadius.md,
          backgroundColor: typeConfig.color,
          color: colors.white,
          fontSize: typography.fontSize.xs,
          fontWeight: typography.fontWeight.bold,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: spacing.sm,
          width: 'auto',
          whiteSpace: 'nowrap',
        }}>
          <span>{typeConfig.icon}</span>
          <span style={{ marginLeft: spacing.xs }}>{typeConfig.label}</span>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          marginBottom: spacing.md,
        }}>
          <div style={{
            width: isMobile ? 60 : 80,
            height: isMobile ? 60 : 80,
            borderRadius: isMobile ? 30 : 40,
            backgroundColor: typeConfig.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: isMobile ? 0 : spacing.md,
            marginBottom: isMobile ? spacing.sm : 0,
            fontSize: isMobile ? 24 : 32,
            fontWeight: 'bold',
            color: colors.white,
          }}>
            {typeConfig.icon}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xl : typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.text,
              marginBottom: spacing.xs,
            }}>
              {chama.name}
            </div>
            <div style={{
              fontSize: typography.fontSize.base,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              {chama.type} • {chama.county}, {chama.town}
            </div>
            <div style={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              padding: `${spacing.xs} ${spacing.sm}`,
              borderRadius: borderRadius.md,
              backgroundColor: getStatusColor(chama.status) + '20',
              width: 'auto',
              whiteSpace: 'nowrap',
            }}>
              <span style={{
                fontSize: typography.fontSize.xs,
                color: getStatusColor(chama.status),
                fontWeight: typography.fontWeight.bold,
                textTransform: 'capitalize',
              }}>
                {chama.status}
              </span>
            </div>
          </div>
        </div>

        {/* Improved Description Section */}
        {chama.description && (
          <div style={{
            marginTop: spacing.lg,
            paddingTop: spacing.md,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}>
            <div style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
              marginBottom: spacing.sm,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}>
              About this {typeConfig.fullLabel}
            </div>
            <div style={{
              fontSize: typography.fontSize.base,
              color: colors.textSecondary,
              lineHeight: '1.5',
              textAlign: 'left',
              letterSpacing: 0.2,
              paddingLeft: spacing.xs,
              paddingRight: spacing.xs,
            }}>
              {chama.description}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStatistics = () => {
    // Extract data from real API response
    const financialStats = statistics?.financial_stats || {};
    const memberStats = statistics?.member_stats || {};
    const activityStats = statistics?.activity_stats || {};
    const chamaInfo = statistics?.chama_info || {};

    // Use real data from statistics API, fallback to chama data
    const walletBalance = financialStats.total_balance || chamaInfo.wallet_balance || chamaInfo.total_funds || chama?.total_funds || 0;
    const totalMembers = memberStats.active_members || memberStats.total_members || chamaInfo.current_members || members.length || 0;
    const maxMembers = chama?.max_members || chamaInfo.max_members || 50;
    const totalMeetings = activityStats.total_meetings || meetings.length || 0;
    const contributionAmount = chama?.contribution_amount || financialStats.monthly_contribution || 0;
    const contributionFrequency = chama?.contribution_frequency || 'Monthly';
    const totalContributions = financialStats.total_contributions || financialStats.total_transactions || 0;
    const activePolls = polls.filter(poll => poll.status === 'active').length || 0;

    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontSize: isMobile ? typography.fontSize.base : typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.text,
          marginBottom: isMobile ? spacing.sm : spacing.md,
          textAlign: 'center',
        }}>
          Chama Statistics
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: isMobile ? spacing.sm : spacing.md,
        }}>
          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            ...getShadowStyle('sm'),
          }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.lg : typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.primary,
            }}>
              {totalMembers}/{maxMembers}
            </div>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Members
            </div>
          </div>

          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            ...getShadowStyle('sm'),
          }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.lg : typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.secondary,
            }}>
              {formatCurrency(walletBalance)}
            </div>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Total Balance
            </div>
          </div>

          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            ...getShadowStyle('sm'),
          }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.lg : typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.success,
            }}>
              {formatCurrency(contributionAmount)}
            </div>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              {contributionFrequency} Contribution
            </div>
          </div>

          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            ...getShadowStyle('sm'),
          }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.lg : typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.warning,
            }}>
              {totalContributions}
            </div>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Total Accounts
            </div>
          </div>

          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            ...getShadowStyle('sm'),
          }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.lg : typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.info,
            }}>
              {totalMeetings}
            </div>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Total Meetings
            </div>
          </div>

          <div style={{
            backgroundColor: colors.surface,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
            ...getShadowStyle('sm'),
          }}>
            <div style={{
              fontSize: isMobile ? typography.fontSize.lg : typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.accent,
            }}>
              {activePolls}
            </div>
            <div style={{
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              Active Polls
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMembers = () => {
    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.md,
        }}>
          <div style={{
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.bold,
            color: colors.text,
          }}>
            Members ({members.length})
          </div>
          {members.length > 5 && (
            <div style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.primary,
              cursor: 'pointer',
            }}>
              View All
            </div>
          )}
        </div>

        {members.length === 0 ? (
          <div style={{
            fontSize: typography.fontSize.base,
            textAlign: 'center',
            fontStyle: 'italic',
            paddingTop: spacing.xl,
            paddingBottom: spacing.xl,
            color: colors.textSecondary,
          }}>
            No members found
          </div>
        ) : (
          <div style={{ gap: spacing.sm }}>
            {members.slice(0, 5).map((member, index) => {
              // Get member name safely - check multiple possible data structures
              const firstName = member.first_name || member.user?.first_name || '';
              const lastName = member.last_name || member.user?.last_name || '';
              const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Member';

              // Parse join date safely
              let joinDate = 'Unknown Date';
              try {
                if (member.joined_at) {
                  const date = new Date(member.joined_at);
                  if (!isNaN(date.getTime())) {
                    joinDate = date.toLocaleDateString();
                  }
                }
              } catch (error) {
                console.log('Error parsing join date:', error);
              }

              // Get status safely - use is_active field from API
              const isActive = member.is_active !== false; // Default to true if not specified
              const memberStatus = isActive ? 'active' : 'inactive';

              // Check if this member is the current user
              const userId = localStorage.getItem('userId');
              const isCurrentUser = member.user_id === userId;

              // Use current user's avatar if this is the current user and we have their profile
              const avatarUrl = isCurrentUser && currentUserProfile?.avatar
                ? currentUserProfile.avatar
                : member.user?.avatar_url;

              return (
                <div
                  key={member.user_id || member.id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: spacing.sm,
                    paddingBottom: spacing.sm,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border,
                    borderBottomStyle: 'solid',
                  }}
                >
                  <div style={{
                    width: isMobile ? 50 : 70,
                    height: isMobile ? 50 : 70,
                    borderRadius: isMobile ? 25 : 35,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: isMobile ? spacing.sm : spacing.md,
                    backgroundColor: avatarUrl ? 'transparent' : colors.primary,
                    fontSize: isMobile ? typography.fontSize.sm : typography.fontSize.base,
                    fontWeight: 'bold',
                    color: colors.white,
                    overflow: 'hidden',
                    backgroundImage: avatarUrl ? `url(${avatarUrl})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}>
                    {avatarUrl ? null : (firstName?.[0] || 'U').toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: isMobile ? typography.fontSize.sm : typography.fontSize.base,
                      fontWeight: typography.fontWeight.medium,
                      color: colors.text,
                      marginBottom: spacing.xs,
                    }}>
                      {fullName} {isCurrentUser ? '(You)' : ''}
                    </div>
                    <div style={{
                      fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
                      color: colors.textSecondary,
                    }}>
                      {member.role || 'Member'} • Joined {joinDate}
                    </div>
                  </div>
                  <div style={{
                    paddingLeft: spacing.sm,
                    paddingRight: spacing.sm,
                    paddingTop: spacing.xs,
                    paddingBottom: spacing.xs,
                    borderRadius: 12,
                    backgroundColor: isActive ? colors.success + '20' : colors.warning + '20',
                  }}>
                    <div style={{
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.medium,
                      color: isActive ? colors.success : colors.warning,
                      textTransform: 'capitalize',
                    }}>
                      {memberStatus}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMeetings = () => {
    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.text,
          marginBottom: spacing.md,
        }}>
          Recent Meetings
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {meetings.slice(0, 3).map((meeting) => (
            <div
              key={meeting.id}
              style={{
                padding: spacing.md,
                borderRadius: borderRadius.md,
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                color: colors.text,
                marginBottom: spacing.xs,
              }}>
                {meeting.title}
              </div>
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                marginBottom: spacing.xs,
              }}>
                {new Date(meeting.scheduledAt).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
              }}>
                <span style={{ fontSize: 14, color: colors.textSecondary }}>👥</span>
                <span style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                }}>
                  {meeting.attendees || 0} attendees
                </span>
                <div style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: meeting.status === 'completed' ? colors.success + '20' : colors.primary + '20',
                }}>
                  <span style={{
                    fontSize: typography.fontSize.xs,
                    color: meeting.status === 'completed' ? colors.success : colors.primary,
                    textTransform: 'capitalize',
                  }}>
                    {meeting.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {meetings.length > 3 && (
          <div style={{ textAlign: 'center', marginTop: spacing.md }}>
            <button style={{
              padding: `${spacing.sm} ${spacing.md}`,
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: colors.primary,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              cursor: 'pointer',
            }}>
              View All Meetings ({meetings.length})
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderTransactions = () => {
    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.text,
          marginBottom: spacing.md,
        }}>
          Recent Transactions
        </div>

        {transactions.length === 0 ? (
          <div style={{
            fontSize: typography.fontSize.base,
            textAlign: 'center',
            fontStyle: 'italic',
            paddingTop: spacing.xl,
            paddingBottom: spacing.xl,
            color: colors.textSecondary,
          }}>
            No transactions found
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {transactions.slice(0, 5).map((transaction) => (
              <div
                key={transaction.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: spacing.sm,
                  borderRadius: borderRadius.md,
                  backgroundColor: colors.backgroundSecondary,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                  <span style={{ fontSize: 20 }}>
                    {transaction.type === 'contribution' ? '💰' : transaction.type === 'withdrawal' ? '📉' : '🔄'}
                  </span>
                  <div>
                    <div style={{
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.medium,
                      color: colors.text,
                    }}>
                      {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                    </div>
                    <div style={{
                      fontSize: typography.fontSize.xs,
                      color: colors.textSecondary,
                    }}>
                      {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'Unknown Date'}
                    </div>
                  </div>
                </div>

                <div style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.bold,
                  color: transaction.type === 'contribution' ? colors.success : transaction.type === 'withdrawal' ? colors.error : colors.primary,
                }}>
                  {transaction.type === 'contribution' ? '+' : transaction.type === 'withdrawal' ? '-' : ''}{formatCurrency(transaction.amount)}
                </div>
              </div>
            ))}
          </div>
        )}

        {transactions.length > 5 && (
          <div style={{ textAlign: 'center', marginTop: spacing.md }}>
            <button style={{
              padding: `${spacing.sm} ${spacing.md}`,
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: colors.primary,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              cursor: 'pointer',
            }}>
              View All Transactions ({transactions.length})
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPolls = () => {
    const activePolls = polls.filter(poll => poll.status === 'active' || poll.is_active);
    if (activePolls.length === 0) return null;

    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.text,
          marginBottom: spacing.md,
        }}>
          Active Polls
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {activePolls.map((poll) => (
            <div
              key={poll.id}
              style={{
                padding: spacing.md,
                borderRadius: borderRadius.md,
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                color: colors.text,
                marginBottom: spacing.xs,
              }}>
                {poll.title}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                }}>
                  Ends: {new Date(poll.end_date).toLocaleDateString()}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}>
                  <span style={{ fontSize: 14, color: colors.textSecondary }}>🗳️</span>
                  <span style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.textSecondary,
                  }}>
                    {poll.total_votes} votes
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRules = () => {
    if (!chama?.rules || chama.rules.length === 0) return null;

    return (
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: borderRadius.lg,
        padding: isMobile ? spacing.md : spacing.lg,
        marginBottom: isMobile ? spacing.md : spacing.lg,
        ...getShadowStyle('md'),
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold,
          color: colors.text,
          marginBottom: spacing.md,
        }}>
          Chama Rules & Regulations
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {chama.rules.map((rule, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: spacing.sm,
                padding: spacing.sm,
                borderRadius: borderRadius.md,
                backgroundColor: colors.backgroundSecondary,
              }}
            >
              <span style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.primary,
                minWidth: 24,
              }}>
                {index + 1}.
              </span>
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.text,
                lineHeight: '1.4',
                flex: 1,
              }}>
                {rule}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAvatarModal = () => {
    if (!showAvatarModal || !selectedAvatarMember) return null;

    const user = selectedAvatarMember.user || {} as User;
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown Member';

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: colors.surface,
          borderRadius: borderRadius.xl,
          padding: 0,
          maxWidth: 400,
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          ...getShadowStyle('lg'),
        }}>
          <div style={{
            position: 'relative',
            width: '100%',
            height: 200,
            backgroundColor: colors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: colors.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 'bold',
              color: colors.primary,
            }}>
              {(user.first_name || 'U')[0].toUpperCase()}
            </div>

            <button
              style={{
                position: 'absolute',
                top: spacing.sm,
                right: spacing.sm,
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => setShowAvatarModal(false)}
            >
              <span style={{ fontSize: 18, color: colors.text }}>×</span>
            </button>
          </div>

          <div style={{
            padding: spacing.lg,
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.bold,
              color: colors.text,
              marginBottom: spacing.xs,
            }}>
              {fullName}
            </div>
            <div style={{
              fontSize: typography.fontSize.base,
              color: colors.textSecondary,
              marginBottom: spacing.md,
            }}>
              {user.email}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: spacing.sm,
            }}>
              <button style={{
                padding: `${spacing.sm} ${spacing.md}`,
                borderRadius: borderRadius.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: 'transparent',
                color: colors.primary,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                cursor: 'pointer',
              }}>
                View Profile
              </button>

              <button style={{
                padding: `${spacing.sm} ${spacing.md}`,
                borderRadius: borderRadius.md,
                border: 'none',
                backgroundColor: colors.primary,
                color: colors.white,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                cursor: 'pointer',
              }}>
                Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: colors.background,
        padding: spacing.md,
      }}>
        <div style={{
          textAlign: 'center',
          padding: spacing.xxxl,
        }}>
          <div style={{
            fontSize: 48,
            color: colors.textTertiary,
            marginBottom: spacing.lg,
          }}>
            ⏳
          </div>
          <div style={{
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.semibold,
            color: colors.text,
          }}>
            Loading chama details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title={chama?.name || 'Chama Details'}
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {renderChamaHeader()}
          {renderStatistics()}

          {/* Smart responsive layout for content cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: isMobile ? spacing.md : spacing.lg,
            marginBottom: isMobile ? spacing.md : spacing.lg,
          }}>
            {renderMembers()}
            {renderMeetings()}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: isMobile ? spacing.md : spacing.lg,
            marginBottom: isMobile ? spacing.md : spacing.lg,
          }}>
            {renderTransactions()}
            {renderPolls()}
          </div>

          {/* Always single column - Rules and Actions */}
          {renderRules()}

          {/* Group Chat Button */}
          <div style={{
            position: 'fixed',
            bottom: isMobile ? spacing.lg : spacing.xl,
            right: isMobile ? spacing.lg : spacing.xl,
            zIndex: 1000,
          }}>
            <button style={{
              padding: isMobile ? `${spacing.sm} ${spacing.md}` : `${spacing.md} ${spacing.lg}`,
              borderRadius: borderRadius.lg,
              border: 'none',
              backgroundColor: colors.success,
              color: colors.white,
              fontSize: isMobile ? typography.fontSize.sm : typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? spacing.xs : spacing.sm,
              minHeight: isMobile ? 44 : 'auto', // Minimum touch target
              minWidth: isMobile ? 44 : 'auto',
            }}>
              <span>💬</span>
              {isMobile ? null : <span>Group Chat</span>}
            </button>
          </div>
        </div>
      </div>

      {renderAvatarModal()}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80, // Space for tab bar
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80, // Space for fixed header
    paddingBottom: 80, // Space for fixed tab bar
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
};

export default ChamaDetails;